import React, { useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { Field, Input } from '../ui/Field';
import type { PasswordVerdict } from '../../types/auth';
interface Props {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  autoComplete?: string;
  /** Omit to render a plain password input with no strength feedback. */
  verdict?: PasswordVerdict;
  error?: string | null;
}
const BAR_TONE = ['bg-danger-700', 'bg-danger-700', 'bg-warn-700', 'bg-ok-700', 'bg-ok-700'] as const;
export function PasswordField({
  id,
  label,
  value,
  onChange,
  hint,
  autoComplete = 'current-password',
  verdict,
  error
}: Props) {
  const [visible, setVisible] = useState(false);
  return <div data-id="element-456" data-mp="1yckwyzx9dzne">
      <Field label={label} htmlFor={id} hint={hint} data-id="element-457" data-mp="1ycum6lahc2s1">
        <div className="relative" data-id="element-458" data-mp="3enf34icj5z8l">
          <Input id={id} type={visible ? 'text' : 'password'} value={value} onChange={event => onChange(event.target.value)} autoComplete={autoComplete} className="pr-14" aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-error` : undefined} data-id="element-459" data-mp="3equh1hugx28f" />
          <button type="button" onClick={() => setVisible(current => !current)} className="absolute right-1 top-1/2 flex min-h-touch min-w-touch -translate-y-1/2 items-center justify-center rounded-md text-ink-muted transition-colors duration-150 ease-out hover:text-ink" aria-label={visible ? 'Hide password' : 'Show password'} data-id="element-460" data-mp="3nftwbc0w1885">
            {visible ? <EyeOffIcon aria-hidden className="h-5 w-5" data-id="element-461" data-mp="33p8c1vzvhbhv" /> : <EyeIcon aria-hidden className="h-5 w-5" data-id="element-462" data-mp="38jmcwdxb0atx" />}
          </button>
        </div>
      </Field>

      {verdict && value.length > 0 && <div className="mt-2" data-id="element-463" data-mp="2g18n91m4fv5x">
          <div className="flex items-center gap-2" data-id="element-464" data-mp="2g0foonxbgbyv">
            <div className="flex h-1.5 flex-1 gap-1" role="img" aria-label={`Password strength: ${verdict.label}`} data-id="element-465" data-mp="3jco3qmwkrllq">
              {[0, 1, 2, 3].map(index => <span key={index} className={`h-full flex-1 rounded-full ${index < verdict.score ? BAR_TONE[verdict.score] : 'bg-line'}`} data-id="element-466" data-mp="3t2z7o9g9dusm" />)}
            </div>
            <span className="text-caption font-medium text-ink-muted" data-id="element-467" data-mp="30fmxkyrabfg7">{verdict.label}</span>
          </div>
          {verdict.problems.length > 0 ? <ul className="mt-1.5 space-y-1" data-id="element-468" data-mp="39fjdobzlz5zn">
              {verdict.problems.map(problem => <li key={problem} className="text-caption text-danger-700" data-id="element-469" data-mp="35btrtuabj2ib">
                  {problem}
                </li>)}
            </ul> : verdict.guidance.map(line => <p key={line} className="mt-1.5 text-caption text-ink-muted" data-id="element-470" data-mp="km4j7z07kcy2">
                {line}
              </p>)}
        </div>}

      {error && <p id={`${id}-error`} className="mt-2 text-body-sm text-danger-700" data-id="element-471" data-mp="x61fc1pwu6bj">
          {error}
        </p>}
    </div>;
}